import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Animated, {
    SharedValue,
    interpolate,
    useAnimatedScrollHandler,
    useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import CheckmarkIcon from '@/assets/icons/checkmark';
import ClockPromo from '@/assets/icons/clock-promo';
import { Button } from '@/components/ButtonComponent';
import RichText, { ParsedTypes } from '@/components/RichText';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Image } from '@/components/lib/components';
import { Box } from '@/components/lib/components/Box';
import { Row } from '@/components/lib/components/Row';
import { GameResponsibly } from '@/feature/invite-friends/components/GameResponsibly';
import { usePromoOptIn } from '@/feature/promohub/hooks/use-submissions';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';
import {
    PromotionDocumentDataBodyNumberedSlice,
    PromotionDocumentDataBodyRichTextSlice,
    PromotionThemeDocument,
} from '@/types/prismic.generated';
import { isIos, isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { findSlice } from '@/utils/parse-prismic-content';
import { formatPromoExpirationTime } from '@/utils/renderDate';

import { usePromoTheme } from '../hooks/use-promo-theme';
import { usePromoConfig, usePromotions } from '../hooks/use-promotions';
import { CLOSE_ICON_PADDING, CLOSE_ICON_SIZE } from '../utils/promotions-utils';
import { CircleBottomGlow } from './CircleBottomGlow';
import { CircleTopGlow } from './CircleTopGlow';

const styles = StyleSheet.create({
    title: {
        fontSize: 40,
        lineHeight: 40,
        // on android, the custom font breaks if we add a fontWeight
        fontWeight: isIos ? '800' : undefined,
        fontFamily: 'gamay-extrabold',
        textAlign: 'center',
        letterSpacing: -1.6,
        textTransform: 'uppercase',
        paddingHorizontal: 8,
    },
    image: {
        width: 160,
        height: 160,
    },
    imageContainer: {
        paddingTop: 21,
    },
    topGlowContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    bottomGlowContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    supportingText: {
        textAlign: 'center',
        marginTop: 5,
        letterSpacing: -0.43,
        lineHeight: 24,
    },
    optInButton: {
        marginBottom: 20,
        borderWidth: 2,
        paddingVertical: 12,
    },
    optInButtonDisabled: {
        backgroundColor: 'transparent',
    },
    expiresIn: {
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    clockIcon: {
        marginRight: 4,
    },
    stepNumber: {
        fontWeight: '700',
    },
    termsAndConditions: {
        textDecorationLine: 'underline',
        textDecorationStyle: 'solid',
    },
    gameResponsibly: {
        paddingTop: 56,
        paddingBottom: 46,
    },
    textShrink: {
        flexShrink: 1,
    },
});

export const PromoDetailsContent = ({
    promoId,
    scrollY,
    headerHeight,
    theme,
}: {
    promoId: string;
    scrollY: SharedValue<number>;
    headerHeight: SharedValue<number>;
    theme: PromotionThemeDocument['data'];
}) => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const paddingTop = isWeb ? 20 : insets.top;
    const { promotions } = usePromotions();
    const { t } = useTranslation('promohub');
    const navigationHeight = insets.top + 2 * CLOSE_ICON_PADDING + CLOSE_ICON_SIZE;

    const {
        background_color,
        promo_title_color,
        bottom_glow_color,
        top_glow_color,
        expiration_tag_color,
        primary_button_background_color,
        primary_button_text_color,
    } = usePromoTheme(theme);

    const promotion = promotions.find(promo => promo.uid === promoId);
    const { displayCTA, cta, postCTA, title, expirationTime, supportingText, termsAndConditionsUrl } =
        usePromoConfig(promotion);
    const { optIn, loading } = usePromoOptIn(
        promoId,
        promotion?.data.segmented ?? false,
        promotion?.data.redirect_path ?? undefined
    );
    const [isOptedIn, setIsOptedIn] = React.useState(promotion?.optedIn);
    const howItWorksSteps = findSlice<PromotionDocumentDataBodyNumberedSlice>('numbered', promotion?.data.body)?.items;
    const details = findSlice<PromotionDocumentDataBodyRichTextSlice>('rich_text', promotion?.data.body)?.items[0]
        .richtexteditor;

    const expirationDays = expirationTime ? formatPromoExpirationTime(expirationTime, t) : undefined;

    const handlePress = async () => {
        try {
            optIn();
            setIsOptedIn(true);
        } catch (error) {
            console.error('Error submitting promotion:', error);
        }
    };

    const handlePressTermsAndConditions = () => {
        if (termsAndConditionsUrl) {
            navigation.navigate('ModalWebView', { uri: termsAndConditionsUrl });
        }
    };

    const scrollHandler = useAnimatedScrollHandler({
        onScroll: event => {
            'worklet';
            scrollY.value = event.contentOffset.y;
        },
    });

    const promoImageOpacity = useAnimatedStyle(() => {
        return {
            opacity: interpolate(
                scrollY.value,
                [navigationHeight, navigationHeight + 2 * CLOSE_ICON_PADDING + CLOSE_ICON_SIZE],
                [1, 0]
            ),
        };
    });

    const innerTopGlowAnimation = useAnimatedStyle(() => {
        return {
            transform: [{ translateY: scrollY.value }],
        };
    });

    const headerOverflow = isWeb ? 'hidden' : undefined;
    return (
        <Animated.ScrollView
            scrollEventThrottle={16}
            showsVerticalScrollIndicator={showScrollIndicator}
            contentContainerStyle={common.grow}
            onScroll={scrollHandler}
        >
            <Box
                style={{ backgroundColor: background_color, overflow: headerOverflow }}
                onLayout={event => {
                    headerHeight.value = event.nativeEvent.layout.height;
                }}
            >
                <Animated.View style={[styles.topGlowContainer, innerTopGlowAnimation]}>
                    <CircleTopGlow color={top_glow_color} headerHeight={headerHeight} />
                </Animated.View>
                <Row
                    alignItems={'flex-start'}
                    justifyContent={'space-between'}
                    style={{ paddingTop }}
                    paddingBottom={'s28'}
                >
                    <SizedBox value={20} />
                    <Box style={styles.imageContainer}>
                        <Animated.View style={promoImageOpacity}>
                            <Image
                                source={{ uri: promotion?.data.promotion_details.url ?? '' }}
                                style={styles.image}
                                resizeMode={'contain'}
                            />
                        </Animated.View>
                    </Box>
                    <SizedBox value={20} />
                </Row>
                <MaxWidthWrapper flex={1}>
                    <Box paddingHorizontal={'s16'}>
                        <Text style={[styles.title, { color: promo_title_color }]}>{title}</Text>
                        {supportingText ? (
                            <Text variant={'bodyLarge'} style={[styles.supportingText, { color: promo_title_color }]}>
                                {supportingText}
                            </Text>
                        ) : null}
                        <SizedBox value={20} />
                        {displayCTA ? (
                            <Button
                                label={
                                    <Row alignItems={'center'} gap={'s8'}>
                                        <Text
                                            variant={'titleLarge'}
                                            style={{
                                                color: isOptedIn
                                                    ? primary_button_background_color
                                                    : primary_button_text_color,
                                            }}
                                            letterSpacing={-0.43}
                                        >
                                            {isOptedIn ? postCTA ?? t('opted_in') : cta ?? t('opt_in')}
                                        </Text>
                                        {isOptedIn ? (
                                            <CheckmarkIcon
                                                width={20}
                                                height={20}
                                                color={primary_button_background_color}
                                            />
                                        ) : null}
                                    </Row>
                                }
                                size={'l'}
                                variant={isOptedIn ? undefined : 'dark'}
                                style={[
                                    styles.optInButton,
                                    {
                                        borderColor: primary_button_background_color,
                                        backgroundColor: isOptedIn ? undefined : primary_button_background_color,
                                    },
                                ]}
                                onPress={handlePress}
                                loading={loading}
                                hierarchy={'primary'}
                                disabled={isOptedIn}
                                disableContainerStyle={styles.optInButtonDisabled}
                            />
                        ) : null}
                        {expirationDays ? (
                            <Row justifyContent={'center'} paddingBottom={'s20'} alignItems={'center'}>
                                <ClockPromo color={expiration_tag_color} style={styles.clockIcon} />
                                <Text
                                    variant={'labelMedium'}
                                    style={[styles.expiresIn, { color: expiration_tag_color }]}
                                >
                                    {expirationDays}
                                </Text>
                            </Row>
                        ) : null}
                    </Box>
                </MaxWidthWrapper>
                <Animated.View style={styles.bottomGlowContainer}>
                    <CircleBottomGlow headerHeight={headerHeight} color={bottom_glow_color} />
                </Animated.View>
            </Box>
            <MaxWidthWrapper flex={1}>
                <Box paddingHorizontal={'s16'} paddingBottom={'s32'} backgroundColor={'gray8'} flex={1}>
                    <Box paddingTop={'s32'}>
                        {howItWorksSteps && howItWorksSteps.length > 0 ? (
                            <>
                                <Text variant={'headlineMedium'} paddingBottom={'s20'}>
                                    {t('how_it_works')}
                                </Text>
                                <Box paddingBottom={'s36'}>
                                    {howItWorksSteps.map((step, index) => (
                                        <Row
                                            paddingBottom={'s16'}
                                            alignItems={'flex-start'}
                                            key={`${step.content}-${index}`}
                                        >
                                            <Box
                                                backgroundColor={'white'}
                                                width={24}
                                                height={24}
                                                borderRadius={'r20'}
                                                alignItems={'center'}
                                                justifyContent={'center'}
                                                marginRight={'s16'}
                                            >
                                                <Text variant={'labelMedium'} color={'gray8'} style={styles.stepNumber}>
                                                    {index + 1}
                                                </Text>
                                            </Box>
                                            <Text style={styles.textShrink} variant={'bodyMedium'} color={'white'}>
                                                {step.content}
                                            </Text>
                                        </Row>
                                    ))}
                                </Box>
                            </>
                        ) : null}
                    </Box>
                    {details && details.length > 0 ? (
                        <>
                            <Text variant={'headlineMedium'} paddingBottom={'s16'}>
                                {t('details')}
                            </Text>
                            <RichText
                                data={details}
                                typeProps={{
                                    [ParsedTypes.P]: {
                                        textAlign: 'left',
                                        color: 'white',
                                        variant: 'bodyMedium',
                                    },
                                }}
                            />
                        </>
                    ) : null}
                    {termsAndConditionsUrl ? (
                        <>
                            <SizedBox value={16} />
                            <Row pb={'s24'}>
                                <TouchableOpacity onPress={handlePressTermsAndConditions} activeOpacity={0.8}>
                                    <Text variant={'bodyMedium'} style={styles.termsAndConditions}>
                                        {t('terms_and_conditions')}
                                    </Text>
                                </TouchableOpacity>
                                <Text variant={'bodyMedium'}>{t('apply')}</Text>
                            </Row>
                        </>
                    ) : null}
                    <GameResponsibly style={styles.gameResponsibly} />
                </Box>
            </MaxWidthWrapper>
        </Animated.ScrollView>
    );
};
